<template>
  <div class="image-library-page">
    <header class="page-heading">
      <div>
        <h1>图片资源库</h1>
        <p>统一管理可供各业务模块复用的图片。文件保存在私有资源库，页面只使用短期展示地址。</p>
      </div>
      <div class="heading-actions">
        <el-button icon="el-icon-refresh" :loading="loading" @click="loadList(false)">刷新</el-button>
        <el-button icon="el-icon-folder-opened" @click="openCategoryDialog">管理分类</el-button>
        <el-button type="primary" icon="el-icon-upload2" @click="openUploadDialog">上传图片</el-button>
      </div>
    </header>

    <el-alert
      title="图片 ID 是稳定业务引用，文件版本和 SHA-256 随编辑结果变化"
      description="覆盖编辑会生成新文件版本、自动切换现有业务引用并保留旧版本；另存会创建新的图片资源。"
      type="info"
      :closable="false"
      show-icon />

    <el-card shadow="never" class="filter-card">
      <div class="filters">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索名称、原文件名、说明或 SHA-256"
          @keyup.enter.native="loadList()"
          @clear="loadList()" />
        <el-select v-model="filters.category_id" clearable filterable placeholder="全部分类" @change="loadList()">
          <el-option v-for="item in options.category_options" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.status" clearable placeholder="全部状态" @change="loadList()">
          <el-option
            v-for="item in options.statuses"
            :key="item.value"
            :label="item.label"
            :value="String(item.value)" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="loadList()">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>
    </el-card>

    <el-card shadow="never" class="library-card">
      <div v-loading="loading" class="image-grid">
        <article
          v-for="item in list"
          :key="item.id"
          class="image-card"
          :class="{ 'image-card--disabled': Number(item.status) !== 1 }">
          <div class="image-preview">
            <el-image
              v-if="accessUrl(item)"
              :src="accessUrl(item)"
              :preview-src-list="[accessUrl(item)]"
              fit="contain"
              lazy>
              <div slot="error" class="image-placeholder">
                <i class="el-icon-picture-outline"></i>
                <span>预览不可用</span>
              </div>
            </el-image>
            <div v-else class="image-placeholder">
              <i class="el-icon-lock"></i>
              <span>临时地址未配置</span>
            </div>
            <el-tag class="status-tag" size="mini" :type="Number(item.status) === 1 ? 'success' : 'info'">
              {{ Number(item.status) === 1 ? '可选择' : '已停用' }}
            </el-tag>
          </div>
          <div class="image-info">
            <div class="image-title" :title="item.name">{{ item.name }}</div>
            <div class="image-category">{{ item.category || '未分类' }}</div>
            <div class="image-meta">
              <span>{{ item.width }} × {{ item.height }}</span>
              <span>{{ formatBytes(item.file_size) }}</span>
              <span>{{ String(item.extension || '').toUpperCase() }}</span>
            </div>
            <div class="image-id">图片 ID {{ item.id }} · 当前文件 {{ item.current_asset_id }}</div>
            <div class="image-version">共 {{ item.version_count || 1 }} 个版本 · {{ formatBytes(item.version_total_size || item.file_size) }}</div>
            <div class="image-hash" :title="item.sha256">{{ item.sha256 }}</div>
            <div class="card-actions">
              <el-button size="mini" type="primary" plain @click="openImageEditDialog(item)">编辑图片</el-button>
              <el-button size="mini" @click="openEditDialog(item)">编辑资料</el-button>
              <el-button size="mini" @click="openVersions(item)">版本历史</el-button>
              <el-button size="mini" type="danger" plain @click="moveToRecycleBin(item)">移到回收站</el-button>
              <el-button size="mini" type="text" @click="copyAssetId(item)">复制图片 ID</el-button>
            </div>
          </div>
        </article>

        <div v-if="!loading && list.length === 0" class="empty-state">
          <i class="el-icon-picture-outline"></i>
          <strong>图片资源库还是空的</strong>
          <span>上传第一张图片后，其他业务模块即可通过稳定图片 ID 复用。</span>
          <el-button type="primary" size="small" @click="openUploadDialog">上传图片</el-button>
        </div>
      </div>

      <el-pagination
        v-if="page.total > 0"
        class="pagination"
        background
        :current-page="page.page_num"
        :page-size="page.page_size"
        :page-sizes="[12, 24, 48, 60]"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange" />
    </el-card>

    <el-dialog
      :title="editingItem ? '编辑图片资源' : '上传图片资源'"
      :visible.sync="uploadDialogVisible"
      width="min(1120px, 94vw)"
      top="4vh"
      :close-on-click-modal="false"
      append-to-body
      @closed="resetUploadForm">
      <el-form v-loading="loadingExistingImage" label-position="top">
        <el-form-item label="图片文件" required>
          <el-upload
            v-if="!uploadFile"
            ref="imageUpload"
            drag
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleUploadFileChange"
            accept="image/jpeg,image/png,image/webp">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到这里，或<em>点击选择</em></div>
          </el-upload>
          <template v-else>
            <div class="selected-file-bar">
              <div class="selected-file-summary">
                <span class="selected-file-icon"><i class="el-icon-check"></i></span>
                <div>
                  <strong>图片已载入，可以开始编辑</strong>
                  <span>所有操作只在浏览器本地进行，点击上传前不会写入资源库。</span>
                </div>
              </div>
              <el-upload
                ref="imageUpload"
                action="#"
                :auto-upload="false"
                :show-file-list="false"
                :on-change="handleUploadFileChange"
                accept="image/jpeg,image/png,image/webp">
                <el-button size="small" icon="el-icon-folder-opened">重新选择</el-button>
              </el-upload>
            </div>
            <ImageUploadEditor
              ref="imageEditor"
              :file="uploadFile"
              @state-change="handleEditorStateChange" />
          </template>
          <div class="upload-limit-tip">
            <i class="el-icon-info"></i>
            支持 JPEG、PNG、WebP；原图与编辑结果均不得超过 {{ options.max_size_mb }} MB，最长边不超过 8192，总像素不超过 4000 万。
          </div>
          <div v-if="editingItem" class="save-mode">
            <div class="save-mode__title">保存方式</div>
            <el-radio-group v-model="saveMode">
              <el-radio label="replace">
                <strong>覆盖当前图片</strong>
                <span>当前条目不变，已有业务引用自动切换到编辑后的版本</span>
              </el-radio>
              <el-radio label="copy">
                <strong>生成新的图片资源</strong>
                <span>保留当前图片，同时创建一个新的资源 ID</span>
              </el-radio>
            </el-radio-group>
          </div>
        </el-form-item>
        <div class="resource-fields">
          <div class="resource-fields__heading">
            <strong>资源信息</strong>
            <span>这些信息用于资源库检索和复用，不会写进图片本身。</span>
          </div>
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="资源名称">
                <el-input v-model.trim="uploadForm.name" maxlength="128" placeholder="留空时使用原文件名" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="分类" required>
                <el-select v-model="uploadForm.category_id" filterable style="width: 100%" placeholder="请选择分类">
                  <el-option v-for="item in options.category_options" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="说明">
            <el-input
              v-model.trim="uploadForm.description"
              type="textarea"
              :rows="2"
              maxlength="500"
              show-word-limit
              placeholder="记录图片用途、来源或设计说明" />
          </el-form-item>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="uploading"
          :disabled="!uploadFile || !editorState.ready"
          icon="el-icon-upload2"
          @click="submitUpload">
          {{ editingItem && saveMode === 'replace' ? '覆盖当前图片' : (editingItem ? '生成新图片' : '上传并入库') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="编辑图片资源信息"
      :visible.sync="editDialogVisible"
      width="560px"
      :close-on-click-modal="false"
      append-to-body>
      <el-form label-position="top">
        <el-form-item label="资源名称" required>
          <el-input v-model.trim="editForm.name" maxlength="128" />
        </el-form-item>
        <el-form-item label="分类">
          <el-select v-model="editForm.category_id" filterable style="width: 100%" placeholder="请选择分类">
            <el-option v-for="item in options.category_options" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model.trim="editForm.description" type="textarea" :rows="3" maxlength="500" show-word-limit />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="状态">
              <el-select v-model="editForm.status" style="width: 100%">
                <el-option :value="1" label="可选择" />
                <el-option :value="0" label="停用" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序值">
              <el-input-number v-model="editForm.sort" :min="0" :max="1000000" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-alert title="停用不会删除文件，也不会破坏已有业务引用。" type="warning" :closable="false" show-icon />
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveEdit">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="图片资源分类管理"
      :visible.sync="categoryDialogVisible"
      width="760px"
      :close-on-click-modal="false"
      append-to-body>
      <div class="category-dialog-heading">
        <span>修改分类名称后，已归类图片会立即显示新名称。</span>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCategoryEdit()">新增分类</el-button>
      </div>
      <el-table v-loading="categoryLoading" :data="categoryList" border>
        <el-table-column prop="name" label="分类名称" min-width="170" />
        <el-table-column label="稳定语义编码" min-width="190">
          <template slot-scope="scope">
            <code v-if="scope.row.semantic_code">{{ scope.row.semantic_code }}</code>
            <span v-else class="muted-text">普通分类</span>
          </template>
        </el-table-column>
        <el-table-column prop="usage_count" label="资源数" width="82" align="center" />
        <el-table-column label="状态" width="86" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="Number(scope.row.status) === 1 ? 'success' : 'info'">
              {{ Number(scope.row.status) === 1 ? '可选择' : '已停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="openCategoryEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="Number(scope.row.is_system) !== 1"
              type="text"
              size="mini"
              class="danger-text"
              @click="deleteCategory(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="categoryDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="categoryForm.id ? '编辑图片分类' : '新增图片分类'"
      :visible.sync="categoryEditVisible"
      width="520px"
      :close-on-click-modal="false"
      append-to-body>
      <el-form label-position="top">
        <el-form-item label="分类名称" required>
          <el-input v-model.trim="categoryForm.name" maxlength="64" />
        </el-form-item>
        <el-form-item v-if="categoryForm.semantic_code" label="稳定语义编码">
          <el-input :value="categoryForm.semantic_code" disabled />
          <div class="form-help">系统用于稳定识别该分类，修改展示名称不会改变此编码。</div>
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model.trim="categoryForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="状态">
              <el-select v-model="categoryForm.status" style="width: 100%">
                <el-option :value="1" label="可选择" />
                <el-option :value="0" label="停用" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序值">
              <el-input-number v-model="categoryForm.sort" :min="0" :max="1000000" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="categoryEditVisible = false">取消</el-button>
        <el-button type="primary" :loading="categorySaving" @click="saveCategory">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="versionTitle" :visible.sync="versionDialogVisible" width="860px" append-to-body>
      <el-alert title="当前版本不会自动清理；旧版本默认保留30天，可恢复或永久保留。" type="info" :closable="false" show-icon />
      <el-table v-loading="versionLoading" :data="versions" stripe class="version-table">
        <el-table-column label="预览" width="82">
          <template slot-scope="scope"><el-image class="version-thumb" :src="accessUrl(scope.row)" fit="contain" :preview-src-list="[accessUrl(scope.row)]" /></template>
        </el-table-column>
        <el-table-column label="文件版本" min-width="230">
          <template slot-scope="scope"><strong>#{{ scope.row.asset_id }} · {{ scope.row.original_name }}</strong><div class="muted-text">{{ scope.row.width }} × {{ scope.row.height }} · {{ formatBytes(scope.row.file_size) }}</div></template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template slot-scope="scope"><el-tag v-if="scope.row.is_current" size="mini" type="success">当前版本</el-tag><el-tag v-else-if="scope.row.keep_forever" size="mini">永久保留</el-tag><el-tag v-else size="mini" type="info">历史版本</el-tag></template>
        </el-table-column>
        <el-table-column label="清理时间" width="170">
          <template slot-scope="scope">{{ scope.row.is_current || scope.row.keep_forever ? '-' : formatTime(scope.row.purge_after) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right">
          <template slot-scope="scope">
            <template v-if="!scope.row.is_current">
              <el-button type="text" @click="restoreVersion(scope.row)">恢复</el-button>
              <el-button type="text" @click="toggleKeepVersion(scope.row)">{{ scope.row.keep_forever ? '取消永久' : '永久保留' }}</el-button>
              <el-button type="text" class="danger-text" @click="purgeVersion(scope.row)">删除</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import apis from '@/api/index'
import ImageUploadEditor from '@/components/assets/ImageUploadEditor.vue'

export default {
  name: 'ImageAssetLibrary',
  components: {
    ImageUploadEditor
  },
  data() {
    return {
      loading: false,
      uploading: false,
      loadingExistingImage: false,
      saving: false,
      categoryLoading: false,
      categorySaving: false,
      uploadDialogVisible: false,
      editingItem: null,
      saveMode: 'replace',
      editDialogVisible: false,
      categoryDialogVisible: false,
      categoryEditVisible: false,
      versionDialogVisible: false,
      versionLoading: false,
      versionItem: null,
      versions: [],
      list: [],
      categoryList: [],
      options: {
        categories: [],
        category_options: [],
        max_size_mb: 10,
        statuses: [
          { value: 1, label: '可选择' },
          { value: 0, label: '已停用' }
        ]
      },
      filters: {
        keyword: '',
        category_id: '',
        status: ''
      },
      page: {
        page_num: 1,
        page_size: 24,
        total: 0
      },
      uploadFile: null,
      uploadSelectionVersion: 0,
      editorState: {
        ready: false,
        edited: false
      },
      uploadForm: {
        name: '',
        category_id: null,
        description: ''
      },
      editForm: {
        id: 0,
        name: '',
        category_id: null,
        description: '',
        status: 1,
        sort: 0
      },
      categoryForm: {
        id: 0,
        name: '',
        semantic_code: '',
        description: '',
        status: 1,
        sort: 0
      }
    }
  },
  computed: {
    versionTitle() {
      return this.versionItem ? `“${this.versionItem.name}”的版本历史` : '图片版本历史'
    }
  },
  async created() {
    await this.loadOptions()
    await this.loadList(false)
  },
  methods: {
    async loadOptions() {
      try {
        const response = await apis.imageAsset.options()
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片选项加载失败')
        const data = response.data.data || {}
        const categoryOptions = Array.isArray(data.category_options)
          ? data.category_options
          : (Array.isArray(data.categories) ? data.categories : [])
            .map((name, index) => ({ id: `legacy-${index}`, name }))
        this.options = { ...this.options, ...data, category_options: categoryOptions }
      } catch (error) {
        this.$message.error(String(error.message || error || '图片选项加载失败'))
      }
    },
    async loadList(resetPage = true) {
      if (resetPage) this.page.page_num = 1
      this.loading = true
      try {
        const response = await apis.imageAsset.list({
          ...this.filters,
          page_num: this.page.page_num,
          page_size: this.page.page_size
        })
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片资源加载失败')
        const data = response.data.data || {}
        this.list = Array.isArray(data.list) ? data.list : []
        this.page.total = Number(data.total || 0)
      } catch (error) {
        this.$message.error(String(error.message || error || '图片资源加载失败'))
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { keyword: '', category_id: '', status: '' }
      this.loadList()
    },
    handlePageSizeChange(size) {
      this.page.page_size = size
      this.loadList()
    },
    handlePageChange(page) {
      this.page.page_num = page
      this.loadList(false)
    },
    accessUrl(item) {
      // 2026-09-16：图片地址由服务端完整返回，禁止拼接后台 /apis 地址。
      return item && item.full_path ? String(item.full_path) : ''
    },
    formatBytes(bytes) {
      const value = Number(bytes || 0)
      if (value < 1024) return `${value} B`
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
      return `${(value / 1024 / 1024).toFixed(2)} MB`
    },
    openUploadDialog() {
      this.resetUploadForm()
      this.uploadDialogVisible = true
    },
    /**
     * 2026-09-10：把已有图片读取为本地 File 后复用同一编辑器。
     * 临时票据只负责本次读取，不会进入编辑结果或数据库。
     */
    async openImageEditDialog(item) {
      this.resetUploadForm()
      this.editingItem = item
      this.saveMode = 'replace'
      this.uploadForm = {
        name: item.name || '',
        category_id: Number(item.category_id) || this.defaultCategoryId(),
        description: item.description || ''
      }
      this.uploadDialogVisible = true
      this.loadingExistingImage = true
      try {
        const response = await fetch(this.accessUrl(item), { credentials: 'same-origin' })
        if (!response.ok) throw new Error('原图片读取失败，请刷新列表后重试')
        const blob = await response.blob()
        const extension = String(item.extension || 'png').toLowerCase()
        this.uploadFile = new File([blob], item.original_name || ((item.name || 'image') + '.' + extension), {
          type: item.mime_type || blob.type,
          lastModified: Date.now()
        })
        this.editorState = { ready: false, edited: false }
      } catch (error) {
        this.$message.error(String(error.message || error || '图片读取失败'))
        this.uploadDialogVisible = false
      } finally {
        this.loadingExistingImage = false
      }
    },
    /** 2026-09-10：关闭或重新打开弹窗时清空本地文件、编辑状态和资源表单，避免串用上一次内容。 */
    resetUploadForm() {
      this.uploadSelectionVersion += 1
      this.uploadFile = null
      this.editingItem = null
      this.saveMode = 'replace'
      this.loadingExistingImage = false
      this.editorState = { ready: false, edited: false }
      this.uploadForm = {
        name: '',
        category_id: this.defaultCategoryId(),
        description: ''
      }
      if (this.$refs.imageUpload) this.$refs.imageUpload.clearFiles()
    },
    /**
     * 2026-09-09：选择后先在浏览器内验证真实尺寸，再把图片交给编辑器。
     * 例如扩展名是 jpg 但内容无法解码时会立即拒绝，不会等上传到服务端才失败。
     */
    async handleUploadFileChange(file) {
      const raw = file && file.raw
      if (!raw) return
      const selectionVersion = ++this.uploadSelectionVersion
      const allowed = ['image/jpeg', 'image/png', 'image/webp']
      const maxMb = Number(this.options.max_size_mb || 10)
      if (allowed.indexOf(raw.type) < 0 || raw.size <= 0 || raw.size > maxMb * 1024 * 1024) {
        this.$message.error('请选择不超过 ' + maxMb + ' MB 的 JPEG、PNG 或 WebP 图片')
        this.discardInvalidSelection()
        return
      }
      try {
        const dimensions = await this.inspectLocalImage(raw)
        if (selectionVersion !== this.uploadSelectionVersion) return
        if (dimensions.width > 8192 || dimensions.height > 8192 || dimensions.width * dimensions.height > 40000000) {
          throw new Error('图片尺寸过大：最长边不能超过 8192，且总像素不能超过 4000 万')
        }
        this.uploadFile = raw
        this.editorState = { ready: false, edited: false }
      } catch (error) {
        if (selectionVersion !== this.uploadSelectionVersion) return
        this.$message.error(String(error.message || error || '无法读取图片内容'))
        this.discardInvalidSelection()
      }
    },
    /**
     * 2026-09-10：无效的新文件只从选择器列表移除；若编辑器里已有图片，则保留现有编辑成果。
     * 例如误选 20 MB 文件时，之前已经裁好的图片不会消失。
     */
    discardInvalidSelection() {
      if (!this.uploadFile) {
        this.clearUploadFile()
        return
      }
      if (this.$refs.imageUpload) this.$refs.imageUpload.clearFiles()
    },
    /** 2026-09-09：统一清空文件和编辑状态，避免换图时误上传上一张图片。 */
    clearUploadFile() {
      this.uploadSelectionVersion += 1
      this.uploadFile = null
      this.editorState = { ready: false, edited: false }
      if (this.$refs.imageUpload) this.$refs.imageUpload.clearFiles()
    },
    /**
     * 2026-09-09：使用浏览器解码图片并读取像素尺寸，临时 URL 无论成功失败都会释放。
     * 返回示例：{ width: 1920, height: 1080 }。
     */
    inspectLocalImage(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
          const dimensions = { width: image.naturalWidth, height: image.naturalHeight }
          URL.revokeObjectURL(url)
          resolve(dimensions)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('图片内容无法解析，文件可能已经损坏'))
        }
        image.src = url
      })
    },
    /** 2026-09-09：接收编辑器轻量状态，用于防止图片尚未加载完成时提交。 */
    handleEditorStateChange(state) {
      this.editorState = {
        ready: Boolean(state && state.ready),
        edited: Boolean(state && state.edited)
      }
    },
    async submitUpload() {
      if (!this.uploadFile) {
        this.$message.warning('请先选择图片文件')
        return
      }
      if (!Number(this.uploadForm.category_id)) {
        this.$message.warning('请选择图片分类')
        return
      }
      this.uploading = true
      try {
        const result = await this.$refs.imageEditor.exportFile()
        const maxBytes = Number(this.options.max_size_mb || 10) * 1024 * 1024
        if (result.file.size <= 0 || result.file.size > maxBytes) {
          throw new Error('编辑后的图片超过 ' + this.options.max_size_mb + ' MB，请降低画质、缩小最长边或改用 WebP')
        }
        if (result.width > 8192 || result.height > 8192 || result.width * result.height > 40000000) {
          throw new Error('编辑后的图片尺寸过大，请缩小输出最长边')
        }
        const formData = new FormData()
        formData.append('image_file', result.file)
        formData.append('name', this.uploadForm.name || '')
        formData.append('category_id', String(this.uploadForm.category_id || ''))
        formData.append('description', this.uploadForm.description || '')
        if (this.editingItem && this.saveMode === 'replace') {
          formData.append('id', String(this.editingItem.id))
        }
        const response = this.editingItem && this.saveMode === 'replace'
          ? await apis.imageAsset.replace(formData)
          : await apis.imageAsset.upload(formData)
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片上传失败')
        this.$message.success(response.data.msg || '图片已上传')
        this.uploadDialogVisible = false
        await this.loadOptions()
        await this.loadList()
      } catch (error) {
        this.$message.error(String(error.message || error || '图片上传失败'))
      } finally {
        this.uploading = false
      }
    },
    openEditDialog(item) {
      this.editForm = {
        id: Number(item.id),
        name: item.name || '',
        category_id: Number(item.category_id) || this.defaultCategoryId(),
        description: item.description || '',
        status: Number(item.status) === 1 ? 1 : 0,
        sort: Number(item.sort || 0)
      }
      this.editDialogVisible = true
    },
    async saveEdit() {
      if (!this.editForm.name) {
        this.$message.warning('图片名称不能为空')
        return
      }
      if (!Number(this.editForm.category_id)) {
        this.$message.warning('请选择图片分类')
        return
      }
      this.saving = true
      try {
        const response = await apis.imageAsset.update({ ...this.editForm })
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片信息保存失败')
        this.$message.success('图片信息已保存')
        this.editDialogVisible = false
        await this.loadOptions()
        await this.loadList(false)
      } catch (error) {
        this.$message.error(String(error.message || error || '图片信息保存失败'))
      } finally {
        this.saving = false
      }
    },
    defaultCategoryId() {
      const categories = this.options.category_options || []
      const fallback = categories.find(item => item.semantic_code === 'uncategorized') || categories[0]
      return fallback ? Number(fallback.id) || null : null
    },
    async openCategoryDialog() {
      this.categoryDialogVisible = true
      await this.loadCategories()
    },
    async loadCategories() {
      this.categoryLoading = true
      try {
        const response = await apis.fileAssetCategory.list({ scope_code: 'image' })
        if (response.data.code !== 1) throw new Error(response.data.msg || '分类加载失败')
        const data = response.data.data || {}
        this.categoryList = Array.isArray(data.list) ? data.list : []
      } catch (error) {
        this.$message.error(String(error.message || error || '分类加载失败'))
      } finally {
        this.categoryLoading = false
      }
    },
    openCategoryEdit(item = null) {
      this.categoryForm = item
        ? {
          id: Number(item.id),
          name: item.name || '',
          semantic_code: item.semantic_code || '',
          description: item.description || '',
          status: Number(item.status) === 1 ? 1 : 0,
          sort: Number(item.sort || 0)
        }
        : { id: 0, name: '', semantic_code: '', description: '', status: 1, sort: 0 }
      this.categoryEditVisible = true
    },
    async saveCategory() {
      if (!this.categoryForm.name) {
        this.$message.warning('分类名称不能为空')
        return
      }
      this.categorySaving = true
      try {
        const payload = {
          id: this.categoryForm.id,
          scope_code: 'image',
          name: this.categoryForm.name,
          description: this.categoryForm.description,
          status: this.categoryForm.status,
          sort: this.categoryForm.sort
        }
        const response = this.categoryForm.id
          ? await apis.fileAssetCategory.update(payload)
          : await apis.fileAssetCategory.create(payload)
        if (response.data.code !== 1) throw new Error(response.data.msg || '分类保存失败')
        this.$message.success(response.data.msg || '分类已保存')
        this.categoryEditVisible = false
        await Promise.all([this.loadCategories(), this.loadOptions()])
        await this.loadList(false)
      } catch (error) {
        this.$message.error(String(error.message || error || '分类保存失败'))
      } finally {
        this.categorySaving = false
      }
    },
    async deleteCategory(item) {
      if (Number(item.usage_count) > 0) {
        this.$message.warning('分类仍被图片使用，请先把图片移动到其他分类')
        return
      }
      try {
        await this.$confirm(`确定删除分类“${item.name}”吗？`, '删除分类', { type: 'warning' })
        const response = await apis.fileAssetCategory.delete({ id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '分类删除失败')
        this.$message.success(response.data.msg || '分类已删除')
        await Promise.all([this.loadCategories(), this.loadOptions()])
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(String(error.message || error || '分类删除失败'))
        }
      }
    },
    formatTime(seconds) {
      return seconds ? new Date(Number(seconds) * 1000).toLocaleString() : '-'
    },
    /** 2026-09-11：打开稳定图片 ID 的完整文件版本历史。 */
    async openVersions(item) {
      this.versionItem = item
      this.versionDialogVisible = true
      this.versionLoading = true
      try {
        const response = await apis.imageAsset.versions({ id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版本历史加载失败')
        this.versions = Array.isArray(response.data.data.list) ? response.data.data.list : []
      } catch (error) {
        this.$message.error(String(error.message || error))
      } finally {
        this.versionLoading = false
      }
    },
    async restoreVersion(version) {
      try { await this.$confirm(`恢复文件版本 #${version.asset_id} 为当前图片？`, '恢复历史版本', { type: 'warning' }) } catch (error) { return }
      const response = await apis.imageAsset.restoreVersion({ id: this.versionItem.id, asset_id: version.asset_id })
      if (response.data.code !== 1) return this.$message.error(response.data.msg || '恢复失败')
      this.$message.success(response.data.msg)
      await Promise.all([this.openVersions(this.versionItem), this.loadList(false)])
    },
    async toggleKeepVersion(version) {
      const response = await apis.imageAsset.keepVersion({ id: this.versionItem.id, asset_id: version.asset_id, keep: version.keep_forever ? 0 : 1 })
      if (response.data.code !== 1) return this.$message.error(response.data.msg || '设置失败')
      this.$message.success(response.data.msg)
      await this.openVersions(this.versionItem)
    },
    async purgeVersion(version) {
      try { await this.$confirm(`彻底删除历史文件 #${version.asset_id}？仍被头像等业务使用时系统会拒绝。`, '删除历史版本', { type: 'error', confirmButtonText: '彻底删除' }) } catch (error) { return }
      const response = await apis.imageAsset.purgeVersion({ id: this.versionItem.id, asset_id: version.asset_id })
      if (response.data.code !== 1) return this.$message.error(response.data.msg || '删除失败')
      this.$message.success(response.data.msg)
      await Promise.all([this.openVersions(this.versionItem), this.loadList(false)])
    },
    /** 先展示真实引用检查结果，再允许把无引用图片移入回收站。 */
    async moveToRecycleBin(item) {
      try {
        const impactResponse = await apis.imageAsset.deleteImpact({ id: item.id })
        if (impactResponse.data.code !== 1) throw new Error(impactResponse.data.msg || '引用检查失败')
        const impact = impactResponse.data.data || {}
        if (!impact.can_delete) {
          const references = impact.references || {}
          const soundNames = (references.sound_cover || []).map(ref => `#${ref.id} ${ref.name}`).join('、')
          const tagNames = (references.tag_icon || []).map(ref => `#${ref.id} ${ref.name}`).join('、')
          const parts = [`音效封面 ${Number((impact.counts || {}).sound_cover || 0)} 处`, `标签图标 ${Number((impact.counts || {}).tag_icon || 0)} 处`]
          if (soundNames) parts.push(`音效：${soundNames}`)
          if (tagNames) parts.push(`标签：${tagNames}`)
          if (Number(impact.external_file_reference_count || 0) > 0) parts.push(`底层文件还有 ${impact.external_file_reference_count} 处头像或其他文件引用`)
          this.$alert(`这张图片仍被使用，不能删除。${parts.join('；')}。`,
            '无法移入回收站',
            { type: 'warning' }
          )
          return
        }
        await this.$confirm(
          `图片“${item.name}”没有业务引用。移入回收站后保留30天，可随时恢复。`,
          '移到回收站',
          { type: 'warning', confirmButtonText: '移到回收站' }
        )
        const response = await apis.imageAsset.moveToRecycleBin({ id: item.id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '移入回收站失败')
        this.$message.success(response.data.msg)
        await this.loadList(false)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(String(error.message || error))
      }
    },
    async copyAssetId(item) {
      const text = String(item.id || '')
      try {
        await navigator.clipboard.writeText(text)
        this.$message.success(`已复制图片 ID ${text}`)
      } catch (error) {
        this.$message.info(`图片 ID：${text}`)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.image-library-page {
  padding: 20px;
  color: #303133;
}

.page-heading {
  display: flex;
  margin-bottom: 18px;
  align-items: flex-start;
  justify-content: space-between;

  h1 { margin: 0; font-size: 24px; }
  p { margin: 8px 0 0; color: #8492a6; font-size: 13px; }
}

.heading-actions { display: flex; gap: 10px; }
.filter-card { margin-top: 16px; }
.library-card { margin-top: 16px; }

.filters {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px 150px auto auto;
  gap: 12px;
}

.image-grid {
  display: grid;
  min-height: 240px;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.image-card {
  overflow: hidden;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: #fff;
  transition: box-shadow .16s ease, transform .16s ease;

  &:hover { box-shadow: 0 8px 24px rgba(40, 55, 80, .1); transform: translateY(-1px); }
}

.image-card--disabled { opacity: .72; }

.image-preview {
  position: relative;
  height: 170px;
  border-bottom: 1px solid #edf0f4;
  background: #f5f7fa;

  .el-image { width: 100%; height: 100%; }
}

.image-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a6aebb;

  i { margin-bottom: 8px; font-size: 30px; }
  span { font-size: 12px; }
}

.status-tag { position: absolute; top: 10px; right: 10px; }
.image-info { padding: 14px; }
.image-title { overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.image-category { margin-top: 6px; color: #5e7fa5; font-size: 12px; }
.image-meta { display: flex; margin-top: 10px; color: #8a94a3; font-size: 11px; gap: 10px; }
.image-id { margin-top: 10px; color: #606266; font-size: 12px; }
.image-version { margin-top: 4px; color: #8492a6; font-size: 11px; }
.image-hash { overflow: hidden; margin-top: 4px; color: #a0a7b2; font-family: monospace; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
/* 2026-09-11：主操作并排、辅助操作独占一行，窄卡片下也不会挤出边界。 */
.card-actions {
  display: grid;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f5;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  .el-button { width: 100%; margin: 0; }
  .el-button:last-child { grid-column: 1 / -1; padding-top: 4px; padding-bottom: 4px; }
}
.version-table { margin-top: 14px; }
.version-thumb { width: 52px; height: 52px; border: 1px solid #ebeef5; border-radius: 6px; background: #f5f7fa; }
.pagination { display: flex; margin-top: 20px; justify-content: flex-end; }
.category-dialog-heading {
  display: flex;
  margin-bottom: 14px;
  align-items: center;
  justify-content: space-between;
  color: #8492a6;
  font-size: 12px;
}
.muted-text { color: #a0a7b2; }
.danger-text { color: #f56c6c; }
.form-help { margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.5; }

.selected-file-bar {
  display: flex;
  margin-bottom: 12px;
  padding: 12px 14px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d7e7f7;
  border-radius: 9px;
  background: #f3f8fd;
}
.selected-file-summary {
  display: flex;
  min-width: 0;
  align-items: center;

  strong { display: block; color: #30445a; font-size: 13px; }
  span { display: block; margin-top: 3px; color: #7d8fa2; font-size: 11px; }
}
.selected-file-icon {
  display: flex !important;
  width: 30px;
  height: 30px;
  margin: 0 10px 0 0 !important;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #67c23a;
  color: #fff !important;
}
.upload-limit-tip {
  margin-top: 9px;
  color: #8a94a3;
  font-size: 11px;
  line-height: 1.5;

  i { margin-right: 4px; color: #6f9dca; }
}
.save-mode {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #dfe5ed;
  border-radius: 8px;
  background: #fafbfd;
}
.save-mode__title { margin-bottom: 9px; color: #344154; font-size: 13px; font-weight: 600; }
.save-mode ::v-deep .el-radio-group { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.save-mode ::v-deep .el-radio {
  display: flex;
  height: auto;
  margin: 0;
  padding: 10px;
  align-items: flex-start;
  border: 1px solid #e4e7ed;
  border-radius: 7px;
  background: #fff;
  white-space: normal;
}
.save-mode ::v-deep .el-radio__label { display: grid; gap: 3px; }
.save-mode ::v-deep .el-radio__label strong { color: #303133; font-size: 12px; }
.save-mode ::v-deep .el-radio__label span { color: #909399; font-size: 10px; line-height: 1.4; }
.resource-fields {
  margin-top: 18px;
  padding: 16px 18px 2px;
  border: 1px solid #e4e8ee;
  border-radius: 10px;
  background: #fafbfd;
}
.resource-fields__heading {
  margin-bottom: 14px;

  strong { color: #344154; font-size: 14px; }
  span { margin-left: 9px; color: #9099a6; font-size: 11px; }
}

.empty-state {
  display: flex;
  min-height: 280px;
  grid-column: 1 / -1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;

  i { margin-bottom: 12px; font-size: 48px; }
  strong { color: #4c5665; font-size: 16px; }
  span { margin: 8px 0 16px; font-size: 12px; }
}

@media (max-width: 1100px) {
  .filters { grid-template-columns: 1fr 1fr; }
  .page-heading { flex-direction: column; }
  .heading-actions { margin-top: 14px; }
  .selected-file-bar { align-items: flex-start; flex-direction: column; gap: 10px; }
}
</style>
